import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import { Info } from "lucide-react";

export const DisclaimerFooter = () => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <footer
        data-testid="disclaimer-footer"
        className="fixed bottom-0 left-0 right-0 z-30 border-t border-[#27272A] bg-[#0A0A0A]/95 backdrop-blur-sm px-4 md:px-6 py-2 flex items-center justify-between gap-4 text-[10px] font-mono text-[#71717A]"
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Info size={11} className="text-[#FF9F0A] flex-shrink-0" />
          <span className="truncate">
            Educational research tool · NOT financial advice · NOT a broker-dealer · You execute all trades in your own account
          </span>
        </div>
        <div className="flex items-center gap-3">
          <button
            data-testid="open-disclaimer-btn"
            onClick={() => setOpen(true)}
            className="hover:text-white transition-colors underline-offset-2 hover:underline"
          >
            Full Disclaimer
          </button>
          <span className="hidden md:inline">·</span>
          <Link
            to="/terms"
            data-testid="footer-terms-link"
            className="hidden md:inline hover:text-white transition-colors underline-offset-2 hover:underline"
          >
            Terms
          </Link>
          <span className="hidden md:inline">·</span>
          <Link
            to="/privacy"
            data-testid="footer-privacy-link"
            className="hidden md:inline hover:text-white transition-colors underline-offset-2 hover:underline"
          >
            Privacy
          </Link>
          <span className="hidden md:inline">·</span>
          <span className="hidden md:inline">ROLL.OPS v0.4</span>
        </div>
      </footer>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          data-testid="disclaimer-modal"
          className="max-w-xl bg-[#0A0A0A] border-[#27272A] text-white"
        >
          <DialogHeader>
            <DialogTitle className="font-bold tracking-tight text-lg">
              Legal Disclaimer
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-xs leading-relaxed text-[#A1A1AA] max-h-[60vh] overflow-y-auto pr-1">
            <Section title="Not Financial Advice">
              ROLL.OPS is an educational and analytical tool for self-directed investors who are
              already familiar with options trading concepts including covered calls, rolling,
              assignment risk, and time decay. Nothing displayed in this application — including
              roll scores, color coding, recommendations, recommendations badges, or any other
              output — constitutes investment, financial, legal, tax, or accounting advice.
            </Section>

            <Section title="Not a Broker — No Execution, No Custody">
              ROLL.OPS does NOT execute trades, route orders, or submit orders to any broker on
              your behalf. ROLL.OPS does NOT hold custody of your funds, securities, or brokerage
              account, and never receives your broker credentials. Any "broker order ticket"
              displayed in this application is informational only — you are responsible for
              entering and verifying every order manually in your own brokerage account. ROLL.OPS
              is NOT a registered broker-dealer or money transmitter.
            </Section>

            <Section title="Not a Registered Advisor">
              The operator of ROLL.OPS is NOT a registered investment advisor, broker-dealer,
              certified financial planner, or licensed financial professional in any jurisdiction.
              No fiduciary relationship is created by your use of this software. Always consult a
              qualified, licensed financial professional before making any investment decision.
            </Section>

            <Section title="Data Accuracy & Sources">
              Market data shown is sourced from third-party providers (currently the Tradier
              Brokerage API for quotes and option chains) and may be delayed, incomplete, stale, or
              inaccurate. Snapshot data may be hours or days old when third-party feeds are
              unavailable; the freshness badge in the heatmap indicates the current source.
              You must independently verify all prices, strikes, premiums, and expirations with
              your broker before executing any trade.
            </Section>

            <Section title="Risk of Loss">
              Options trading involves substantial risk including the risk of total loss. Covered
              calls can cap upside, trigger assignment, and generate tax events. Rolling options
              can lock in losses or create unintended exposure. Past performance and theoretical
              calculations shown here do not predict future results. Only trade with money you can
              afford to lose.
            </Section>

            <Section title="No Liability">
              By using ROLL.OPS you agree that the operator, contributors, and any affiliated
              parties shall have NO liability for any direct, indirect, incidental, consequential,
              or any other damages arising from your use of this application, reliance on its
              output, or trading decisions made on the basis of information shown here. The
              software is provided "AS IS" without warranty of any kind, express or implied.
            </Section>

            <Section title="Jurisdiction">
              This application makes no representation that its content is appropriate or
              available for use in any particular jurisdiction. Users are responsible for
              compliance with all applicable local laws and regulations regarding securities and
              options trading.
            </Section>

            <Section title="Privacy">
              We store your Google account identifier, email, name, profile picture, the positions
              you save, the tickers you track, and a log of actions you perform (login, position
              create/delete, ticker load) for the purpose of operating the service. We do not
              sell your data to third parties.
            </Section>

            <div className="border-t border-[#27272A] pt-3 mt-3 text-[10px] text-[#71717A]">
              By continuing to use ROLL.OPS you acknowledge that you have read, understood, and
              accepted this disclaimer in full.
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

const Section = ({ title, children }) => (
  <div>
    <h3 className="text-white font-semibold text-xs mb-1 tracking-tight">{title}</h3>
    <p>{children}</p>
  </div>
);
